import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	type Chat,
	isInProgressStreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { createProvisionalConversationAgent } from '@atlassian/conversation-assistant-store/controllers/chat/conversation-agent-identity';
import {
	CHAT_STORE_STATUS,
	StoreActionLoggableName,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { clearStatus } from './clearStatus';
import { createSafeAction, type SafeAction } from './createSafeAction';
import { extractAgentVersionFromMessages } from './extractAgentVersionFromMessages';
import { hydrateConversationSpaces } from './hydrateConversationSpaces';
import { hydrateScheduledChatRuleStatuses } from './hydrateScheduledChatRuleStatuses';
import { initializeConversationData } from './initializeConversationData';
import { isAwaitingReply } from './isAwaitingReply';
import { normalizeRestorableRequestedMode } from './normalizeRestorableRequestedMode';
import { preserveTrailingFollowUps } from './preserveTrailingFollowUps';
import { processConversationMessages } from './processConversationMessages';
import { setConversationData } from './setConversationData';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import type { ChatStoreAction, WarmConversationCacheArgs } from './types';

// Only fetchConversation reconnects to a running turn, so a cached copy of one would be shown as a
// static snapshot, or as an empty chat if the turn's messages are not persisted yet.
const hasSettledHistory = (latestMessages: Chat): boolean =>
	!isAwaitingReply(latestMessages) && !isInProgressStreamResponse(latestMessages.at(-1));

export const warmConversationCache: SafeAction<[WarmConversationCacheArgs]> = createSafeAction(
	StoreActionLoggableName.warmConversationCache,
	({
		conversation: conversationParams,
		forceRefresh,
		onSuccess,
		onError,
	}: WarmConversationCacheArgs): ChatStoreAction =>
		async (
			{ getState, setState, dispatch },
			{
				assistanceService,
				experienceTracker,
				sendAnalyticsEvent,
				getMessages,
				setMessages,
				capabilities,
				onConversationSpacesHydrated,
				scheduledChatStudioInfoParams,
				getConversationHydrationScopeKey,
			},
		) => {
			const conversationId = conversationParams.id;

			const existingConversation = getState().conversations[conversationId];

			// Skip if conversation is actively in-flight
			if (
				existingConversation?.status === CHAT_STORE_STATUS.loadingChat ||
				existingConversation?.status === CHAT_STORE_STATUS.awaitingResponse ||
				existingConversation?.status === CHAT_STORE_STATUS.typingResponse
			) {
				return;
			}

			// Skip if conversation is already loaded (idle with messages) unless forceRefresh
			if (existingConversation?.status === CHAT_STORE_STATUS.idle && !forceRefresh) {
				return;
			}

			try {
				experienceTracker.start({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
					id: conversationId,
					attributes: {
						conversationId,
						forceRefresh: Boolean(forceRefresh),
					},
				});

				// Bootstrap conversation in store with provided metadata or client defaults.
				const conversationAgentForWarmLoadingReset = isExperimentEnabled('rovo_chat_mention_agents')
					? (existingConversation?.agent ??
						createProvisionalConversationAgent(
							conversationParams.agentId ?? existingConversation?.sourceAgentId,
						))
					: undefined;
				const conversationAgentFieldsForWarmLoadingReset = conversationAgentForWarmLoadingReset
					? { agent: conversationAgentForWarmLoadingReset }
					: {};

				const messagesBeforeReload = getMessages(conversationId);
				const hydrationScopeKey = getConversationHydrationScopeKey?.();

				dispatch(
					initializeConversationData({
						conversationId,
						data: {
							id: conversationId,
							name: conversationParams.name ?? existingConversation?.name ?? '',
							timeCreated:
								conversationParams.timeCreated ??
								existingConversation?.timeCreated ??
								new Date().toISOString(),
							status: CHAT_STORE_STATUS.loadingChat,
							traces: [],
							sourceAgentId:
								conversationParams.agentId ?? existingConversation?.sourceAgentId ?? '',
							...conversationAgentFieldsForWarmLoadingReset,
							messages: [],
						},
					}),
				);

				// If metadata params not fully provided, fetch from API in parallel (silent failure)
				const hasFullMetadata =
					conversationParams.name != null && conversationParams.timeCreated != null;

				const metadataPromise: Promise<void> = hasFullMetadata
					? Promise.resolve()
					: assistanceService
							.fetchConversationMetadata(conversationId)
							.then((metadata) => {
								if (isExperimentEnabled('rovo_chat_spaces_experiment')) {
									hydrateConversationSpaces({
										conversationId,
										metadata,
										dispatch,
										onConversationSpacesHydrated,
									});
								}
								const fetchedConversationAgentFields =
									metadata.agent && isExperimentEnabled('rovo_chat_mention_agents')
										? { agent: metadata.agent }
										: {};

								dispatch(
									setConversationData({
										conversationId,
										data: {
											name: metadata.name ?? conversationParams.name ?? '',
											timeCreated:
												metadata.time_created ??
												conversationParams.timeCreated ??
												existingConversation?.timeCreated ??
												new Date().toISOString(),
											sourceAgentId:
												metadata.agent?.agent_id ??
												metadata.agent?.agent_named_id ??
												conversationParams.agentId ??
												existingConversation?.sourceAgentId ??
												'',
											...fetchedConversationAgentFields,
											...(metadata.tags !== undefined ? { tags: metadata.tags } : {}),
											lastRequestedMode: normalizeRestorableRequestedMode(
												metadata.last_requested_mode,
											),
										},
									}),
								);
							})
							.catch(() => {
								// Silent failure — keep client defaults
							});

				// Run messages fetch and metadata fetch in parallel; wait for both
				const chatPromise = assistanceService.getChat(conversationId, capabilities);

				const [chatResult] = await Promise.allSettled([chatPromise, metadataPromise]);

				if (chatResult.status === 'rejected') {
					const normalizedError =
						chatResult.reason instanceof Error
							? chatResult.reason
							: new Error(String(chatResult.reason));
					experienceTracker.fail({
						teamName: 'Rovo Chat',
						name: ExperienceName.WARM_CONVERSATION_CACHE,
						error: normalizedError,
					});
					dispatch(
						setConversationData({
							conversationId,
							data: clearStatus({}),
						}),
					);
					onError?.(normalizedError);
					return;
				}

				const serviceChat = chatResult.value;

				const status = getState().conversations[conversationId]?.status;

				if (status !== CHAT_STORE_STATUS.loadingChat) {
					return;
				}

				if (fg('rovo_chat_fetch_waits_for_running_turn') && !hasSettledHistory(serviceChat)) {
					const { [conversationId]: _warmedConversation, ...otherConversations } =
						getState().conversations;
					setState({
						conversations: existingConversation
							? { ...otherConversations, [conversationId]: existingConversation }
							: otherConversations,
					});
					setMessages(conversationId, messagesBeforeReload);
					experienceTracker.succeed({
						name: ExperienceName.WARM_CONVERSATION_CACHE,
					});
					onSuccess?.();
					return;
				}

				const normalizedMessages = processConversationMessages({
					latestMessages: serviceChat,
					conversationId,
					sendAnalyticsEvent,
					shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
						getState(),
						conversationId,
					),
				});

				const messages = await hydrateScheduledChatRuleStatuses({
					messages: normalizedMessages,
					getCachedMessages: () => [...messagesBeforeReload, ...getMessages(conversationId)],
					studioInfoParams: scheduledChatStudioInfoParams,
				});
				if (
					getState().conversations[conversationId]?.status !== CHAT_STORE_STATUS.loadingChat ||
					(getConversationHydrationScopeKey &&
						getConversationHydrationScopeKey() !== hydrationScopeKey)
				) {
					return;
				}

				const fetchedAgentVersion = extractAgentVersionFromMessages(serviceChat);

				dispatch(
					setConversationData({
						conversationId,
						data: {
							...clearStatus({ lastAgentMessageStatus: 'SUCCESSFUL' }),
							...(fetchedAgentVersion ? { agentVersion: fetchedAgentVersion } : {}),
						},
					}),
				);

				setMessages(conversationId, preserveTrailingFollowUps(messagesBeforeReload, messages));

				experienceTracker.succeed({
					name: ExperienceName.WARM_CONVERSATION_CACHE,
				});

				onSuccess?.();
			} catch (e) {
				const normalizedError = e instanceof Error ? e : new Error(String(e));
				experienceTracker.fail({
					teamName: 'Rovo Chat',
					name: ExperienceName.WARM_CONVERSATION_CACHE,
					error: normalizedError,
				});
				onError?.(normalizedError);
			}
		},
	{
		experienceName: () => ExperienceName.WARM_CONVERSATION_CACHE,
		actionErrorHandler: () => {
			// Suppress all default error handling for cache warming
			return;
		},
	},
);
